import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Folder, Inbox, ListChecks, Moon, Tag as TagIcon, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBackend } from "../backend/BackendProvider";
import { dateInTimeZone } from "../app/userDate";
import { mutationContext } from "../backend/mutation";
import type { ScheduleKind, Tag, TaskSummary } from "../domain/entities";
import { Dialog } from "./Dialog";
import { useToast } from "./ToastProvider";
import { errorText } from "../backend/errorPresentation";
import { enqueueOfflineTask } from "../app/offline";

const scheduleOptions = [
  ["inbox", "Inbox", Inbox],
  ["today", "Today", CalendarDays],
  ["date", "Pick a Date", CalendarDays],
  ["anytime", "Anytime", Folder],
  ["someday", "Someday", Moon],
] as const;

type QuickAddResult = { kind: "queued"; title: string } | { kind: "created"; task: TaskSummary };

export function QuickAddDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const backend = useBackend();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { show } = useToast();
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const dateOptionRef = useRef<HTMLButtonElement>(null);
  const [titleFocusRequest, setTitleFocusRequest] = useState(0);
  const [title, setTitle] = useState("");
  const [scheduleKind, setScheduleKind] = useState<ScheduleKind>("inbox");
  const [locationValue, setLocationValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState("");
  const [scheduleBeforeDate, setScheduleBeforeDate] = useState<{ scheduleKind: ScheduleKind; startDate: string } | null>(null);
  const [dateAnnouncement, setDateAnnouncement] = useState("");
  const [deadline, setDeadline] = useState("");
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [tagActiveIndex, setTagActiveIndex] = useState(0);
  const [tagAnnouncement, setTagAnnouncement] = useState("");
  const areas = useQuery({ queryKey: ["areas"], queryFn: () => backend.areas.list(), enabled: open });
  const projects = useQuery({ queryKey: ["projects", "quick-add"], queryFn: () => backend.projects.list({ attentions: ["starting", "active", "recently_finished", "quiet", "someday"] }, "name", { limit: 200 }), enabled: open });
  const tags = useQuery({ queryKey: ["tags"], queryFn: () => backend.tags.list(), enabled: open });
  const preferences = useQuery({ queryKey: ["preferences"], queryFn: () => backend.preferences.get(), enabled: open });
  const inheritedLocation = useMemo(() => locationFromPath(location.pathname), [location.pathname]);
  const timezone = preferences.data?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const selectedTags = useMemo(() => (tags.data ?? []).filter((tag) => tagIds.includes(tag.id)), [tagIds, tags.data]);
  const normalizedTagInput = normalizeTagName(tagInput);
  const matchingTags = useMemo(() => {
    const allTags = tags.data ?? [];
    if (!normalizedTagInput) return allTags.slice(0, 8);
    return allTags.filter((tag) => normalizeTagName(tag.name).includes(normalizedTagInput)).slice(0, 8);
  }, [normalizedTagInput, tags.data]);
  const exactTag = useMemo(() => (tags.data ?? []).find((tag) => normalizeTagName(tag.name) === normalizedTagInput), [normalizedTagInput, tags.data]);
  const tagOptions = useMemo(() => [
    ...matchingTags.map((tag) => ({ kind: "existing" as const, tag })),
    ...(tagInput.trim() && !exactTag ? [{ kind: "create" as const, name: tagInput.trim() }] : []),
  ], [exactTag, matchingTags, tagInput]);

  useEffect(() => {
    if (!open || title || locationValue || !inheritedLocation) return;
    setLocationValue(inheritedLocation);
  }, [inheritedLocation, locationValue, open, title]);

  useEffect(() => {
    if (!open || title) return;
    const inheritedSchedule = scheduleFromPath(location.pathname);
    if (inheritedSchedule) setScheduleKind(inheritedSchedule);
    const inheritedDate = new URLSearchParams(location.search).get("date");
    if (location.pathname === "/upcoming" && inheritedDate) { setStartDate(inheritedDate); setScheduleKind("date"); }
  }, [location.pathname, location.search, open, title]);

  const reset = () => {
    setTitle(""); setScheduleKind("inbox"); setLocationValue(""); setStartDate(""); setDeadline("");
    setNotes(""); setChecklist(""); setTagIds([]); setTagInput("");
    setDatePickerOpen(false); setPendingDate(""); setScheduleBeforeDate(null); setDateAnnouncement("");
    setTagPickerOpen(false); setTagActiveIndex(0); setTagAnnouncement(""); createTag.reset();
  };
  const createTag = useMutation<Tag, Error, string>({
    mutationFn: async (rawName) => {
      const name = rawName.trim();
      const latestTags = await backend.tags.list();
      const existing = latestTags.find((tag) => normalizeTagName(tag.name) === normalizeTagName(name));
      if (existing) return existing;
      try {
        return await backend.tags.create({ name, color: null }, mutationContext());
      } catch (error) {
        const refreshedTags = await backend.tags.list();
        const concurrentMatch = refreshedTags.find((tag) => normalizeTagName(tag.name) === normalizeTagName(name));
        if (concurrentMatch) return concurrentMatch;
        throw error;
      }
    },
    onSuccess: (tag) => {
      queryClient.setQueryData<Tag[]>(["tags"], (current = []) => {
        const withoutDuplicate = current.filter((item) => normalizeTagName(item.name) !== normalizeTagName(tag.name));
        return [...withoutDuplicate, tag].sort((a, b) => a.name.localeCompare(b.name, "en-US"));
      });
      setTagIds((current) => current.includes(tag.id) ? current : [...current, tag.id]);
      setTagInput("");
      setTagPickerOpen(false);
      setTagAnnouncement(`Selected Tag: ${tag.name}`);
      requestAnimationFrame(() => tagInputRef.current?.focus());
    },
  });
  const mutation = useMutation<QuickAddResult, Error, boolean>({
    mutationFn: (_continueAdding: boolean) => {
      const [locationType, locationId] = locationValue.split(":");
      const timezone = preferences.data?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      const effectiveStartDate = startDate || (scheduleKind === "today" ? dateInTimeZone(new Date(), timezone) : "");
      const input = {
        title: title.trim(),
        notes: notes.trim(),
        scheduleKind,
        startDate: effectiveStartDate || null,
        deadline: deadline || null,
        reminderAt: null,
        areaId: locationType === "area" ? locationId ?? null : null,
        projectId: locationType === "project" ? locationId ?? null : null,
        tagIds,
        checklist: checklist.split("\n").map((item) => item.trim()).filter(Boolean),
      };
      if (!navigator.onLine) { enqueueOfflineTask(input); return Promise.resolve({ kind: "queued" as const, title: input.title }); }
      return backend.tasks.create(input, mutationContext()).then((task) => ({ kind: "created" as const, task }));
    },
    onSuccess: async (result, continueAdding: boolean) => {
      const createdTitle = result.kind === "created" ? result.task.title : result.title;
      if (result.kind === "queued") {
        if (continueAdding) { const preservedLocation = locationValue; const preservedSchedule = scheduleKind; const preservedStartDate = startDate; reset(); setLocationValue(preservedLocation); setScheduleKind(preservedSchedule); setStartDate(preservedStartDate); setTitleFocusRequest((request) => request + 1); }
        else { reset(); onClose(); }
        show(`“${createdTitle}” was saved offline and will sync when you reconnect.`);
        return;
      }
      const task = result.task;
      await queryClient.invalidateQueries();
      if (continueAdding) {
        const preservedLocation = locationValue; const preservedSchedule = scheduleKind; const preservedStartDate = startDate;
        reset(); setLocationValue(preservedLocation); setScheduleKind(preservedSchedule); setStartDate(preservedStartDate);
        show(`“${task.title}” was created. You can add another Task.`);
        setTitleFocusRequest((request) => request + 1);
        return;
      }
      const destination = taskDetailDestination(location.pathname, scheduleKind);
      const destinationParams = new URLSearchParams({ task: task.id });
      if (task.scheduleKind === "date" && task.startDate) destinationParams.set("date", task.startDate);
      reset(); onClose(); show(`“${task.title}” was created.`);
      navigate(`${destination}?${destinationParams.toString()}`);
    },
  });
  useEffect(() => {
    if (!open || titleFocusRequest === 0 || mutation.isPending) return;
    const frame = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [mutation.isPending, open, titleFocusRequest]);
  useEffect(() => resizeTextarea(titleRef.current, 2), [title]);
  useEffect(() => resizeTextarea(notesRef.current), [notes]);
  const submit = (continueAdding = false) => { if (title.trim() && !mutation.isPending && !createTag.isPending) mutation.mutate(continueAdding); };
  useEffect(() => {
    if (!open) return;
    const keydown = (event: KeyboardEvent) => {
      if (event.isComposing || event.key !== "Enter" || (!event.metaKey && !event.ctrlKey) || event.altKey) return;
      event.preventDefault();
      submit(event.shiftKey);
    };
    document.addEventListener("keydown", keydown);
    return () => document.removeEventListener("keydown", keydown);
  }, [createTag.isPending, mutation.isPending, open, title]);
  const requestClose = () => {
    if (mutation.isPending) { show("The Task is being created. Please wait."); return; }
    if (createTag.isPending) { show("The Tag is being created. Please wait."); return; }
    if (hasDraft({ title, locationValue, startDate, deadline, notes, checklist, tagIds, tagInput })) {
      const preserve = window.confirm("Keep this draft? Choose Cancel to discard it.");
      if (preserve) show("Draft kept. You can continue next time.");
      else { reset(); show("Draft discarded."); }
    }
    onClose();
  };
  const selectTag = (tag: Tag) => {
    setTagIds((current) => current.includes(tag.id) ? current : [...current, tag.id]);
    setTagInput("");
    setTagPickerOpen(false);
    setTagAnnouncement(tagIds.includes(tag.id) ? `Tag ${tag.name} is already selected.` : `Selected Tag: ${tag.name}`);
    createTag.reset();
    requestAnimationFrame(() => tagInputRef.current?.focus());
  };
  const removeTag = (tag: Tag) => {
    setTagIds((current) => current.filter((id) => id !== tag.id));
    setTagAnnouncement(`Removed Tag: ${tag.name}`);
    requestAnimationFrame(() => tagInputRef.current?.focus());
  };
  const chooseTagOption = (index: number) => {
    const option = tagOptions[index];
    if (!option) return;
    if (option.kind === "existing") selectTag(option.tag);
    else if (!createTag.isPending) createTag.mutate(option.name);
  };
  const handleTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setTagPickerOpen(true);
      setTagActiveIndex((current) => {
        if (!tagOptions.length) return 0;
        return event.key === "ArrowDown" ? (current + 1) % tagOptions.length : (current - 1 + tagOptions.length) % tagOptions.length;
      });
    } else if (event.key === "Enter" && tagPickerOpen && tagOptions.length) {
      event.preventDefault();
      chooseTagOption(tagActiveIndex);
    } else if (event.key === "Escape" && tagPickerOpen) {
      event.preventDefault();
      event.stopPropagation();
      setTagPickerOpen(false);
    }
  };
  const openDatePicker = () => {
    if (!datePickerOpen) setScheduleBeforeDate({ scheduleKind, startDate });
    setPendingDate(startDate);
    setDatePickerOpen(true);
    setDateAnnouncement("Date picker opened.");
    requestAnimationFrame(() => dateInputRef.current?.focus());
  };
  const closeDatePicker = (confirm: boolean) => {
    if (confirm) {
      if (!pendingDate) { setDateAnnouncement("Choose a date."); dateInputRef.current?.focus(); return; }
      setStartDate(pendingDate); setScheduleKind("date"); setDateAnnouncement(`Selected ${pendingDate}`);
    } else if (scheduleBeforeDate) {
      setScheduleKind(scheduleBeforeDate.scheduleKind); setStartDate(scheduleBeforeDate.startDate); setDateAnnouncement("Date selection cancelled. The rest of the draft is unchanged.");
    }
    setDatePickerOpen(false); setScheduleBeforeDate(null);
    requestAnimationFrame(() => dateOptionRef.current?.focus());
  };

  return (
    <Dialog variant="quick-add" open={open} title="New Task" onClose={requestClose} footer={<><span className="dialog-hint">⌘/Ctrl + Enter Create · ⌘/Ctrl + Shift + Enter Save and Continue · Esc Close</span><div className="button-group"><button data-interaction="task.create-continue" className="button" disabled={!title.trim() || mutation.isPending || createTag.isPending} onClick={() => submit(true)}>Save and Continue</button><button data-interaction="task.create" className="button primary" disabled={!title.trim() || mutation.isPending || createTag.isPending} onClick={() => submit(false)}>{mutation.isPending ? "Creating…" : "Create and Open"}</button></div></>}>
      <fieldset className="quick-add-form" disabled={mutation.isPending}>
        <label className="sr-only" htmlFor="quick-title">Title</label>
        <textarea ref={titleRef} data-dialog-initial-focus id="quick-title" className="quick-title-input" rows={1} placeholder="What needs to be done?" value={title} onChange={(event) => setTitle(event.target.value)} />
        <div className="quick-add-columns">
          <section className="quick-add-column quick-content-column" aria-labelledby="quick-content-title">
            <h3 id="quick-content-title">Content</h3>
            <div className="quick-advanced">
              <label className="quick-field"><span>Notes</span><textarea ref={notesRef} className="quick-textarea quick-notes-input" rows={6} placeholder="Add context, links, or contact details…" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
              <label className="quick-field"><span><ListChecks size={14} />Checklist</span><textarea className="quick-textarea" rows={3} placeholder="One item per line" value={checklist} onChange={(event) => setChecklist(event.target.value)} /></label>
            </div>
          </section>
          <section className="quick-add-column quick-planning-column" aria-labelledby="quick-planning-title">
            <h3 id="quick-planning-title">Planning</h3>
            <div className="quick-advanced">
              <div className="quick-field"><span>When</span><div className="quick-options" aria-label="When">{scheduleOptions.map(([value, label, Icon]) => <button ref={value === "date" ? dateOptionRef : undefined} type="button" key={value} className={`chip ${scheduleKind === value ? "selected" : ""}`} aria-pressed={scheduleKind === value} aria-expanded={value === "date" ? datePickerOpen : undefined} onClick={() => { if (value === "date") openDatePicker(); else { setDatePickerOpen(false); setScheduleBeforeDate(null); setScheduleKind(value); } }}><Icon size={14} />{label}</button>)}</div>{datePickerOpen && <div className="quick-date-picker" role="group" aria-label="Pick a Date" onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); closeDatePicker(false); } }}><input ref={dateInputRef} aria-label="Date" className="input" type="date" value={pendingDate} onChange={(event) => setPendingDate(event.target.value)} /><div className="button-group"><button type="button" className="button small" onClick={() => closeDatePicker(false)}>Cancel</button><button type="button" className="button small primary" disabled={!pendingDate} onClick={() => closeDatePicker(true)}>Confirm Date</button></div></div>}<span className="sr-only" role="status" aria-live="polite">{dateAnnouncement}</span></div>
              <label className="quick-field"><span>Start Date</span><input aria-label="Start Date" className="input" type="date" value={startDate} onChange={(event) => { setStartDate(event.target.value); if (event.target.value) setScheduleKind("date"); }} /></label>
              <label className="quick-field"><span>Deadline</span><input aria-label="Deadline" className="input" type="date" value={deadline} onChange={(event) => setDeadline(event.target.value)} /></label>
              <label className="quick-field"><span>Project / Area</span><select className="select" aria-label="Project / Area" value={locationValue} onChange={(event) => setLocationValue(event.target.value)}><option value="">None</option>{Boolean(areas.data?.length) && <optgroup label="Areas">{areas.data?.map((area) => <option key={area.id} value={`area:${area.id}`}>{area.name}</option>)}</optgroup>}{Boolean(projects.data?.items.length) && <optgroup label="Projects">{projects.data?.items.map((project) => <option key={project.id} value={`project:${project.id}`}>{project.name}</option>)}</optgroup>}</select>{(areas.isError || projects.isError) && <span className="quick-inline-error" role="alert">Some Projects or Areas could not load. <button type="button" onClick={() => { void areas.refetch(); void projects.refetch(); }}>Retry</button></span>}</label>
              <div className="quick-field quick-tag-field" data-interaction="tag.manage">
                <span>Tag</span>
                {selectedTags.length > 0 && <div className="quick-selected-tags" aria-label="Selected Tags">{selectedTags.map((tag) => <span className="quick-selected-tag" key={tag.id}><TagIcon size={13} />{tag.name}<button type="button" aria-label={`Remove Tag ${tag.name}`} onClick={() => removeTag(tag)}><X size={13} /></button></span>)}</div>}
                <div className="quick-tag-combobox">
                  <input ref={tagInputRef} className="input" role="combobox" aria-label="Search or create a Tag" aria-autocomplete="list" aria-expanded={tagPickerOpen} aria-controls="quick-tag-options" aria-activedescendant={tagPickerOpen && tagOptions[tagActiveIndex] ? `quick-tag-option-${tagActiveIndex}` : undefined} placeholder="Search or create a Tag" value={tagInput} onFocus={() => setTagPickerOpen(true)} onChange={(event) => { setTagInput(event.target.value); setTagPickerOpen(true); setTagActiveIndex(0); createTag.reset(); setTagAnnouncement(""); }} onKeyDown={handleTagKeyDown} />
                  {tagPickerOpen && <div id="quick-tag-options" className="quick-tag-options" role="listbox" aria-label="Tag options">{tags.isLoading ? <small>Loading Tags…</small> : tags.isError ? <small className="quick-inline-error" role="alert">Unable to load Tags. <button type="button" onClick={() => void tags.refetch()}>Retry</button></small> : tagOptions.length ? tagOptions.map((option, index) => option.kind === "existing" ? <button id={`quick-tag-option-${index}`} role="option" aria-selected={tagIds.includes(option.tag.id)} type="button" className={index === tagActiveIndex ? "active" : ""} key={option.tag.id} onMouseDown={(event) => event.preventDefault()} onClick={() => selectTag(option.tag)}><TagIcon size={14} /><span>{option.tag.name}</span>{tagIds.includes(option.tag.id) && <small>Selected</small>}</button> : <button id={`quick-tag-option-${index}`} role="option" aria-selected="false" type="button" className={index === tagActiveIndex ? "active" : ""} key={`create-${option.name}`} disabled={createTag.isPending} onMouseDown={(event) => event.preventDefault()} onClick={() => createTag.mutate(option.name)}><span>Create “{option.name}”</span></button>) : <small>No matching Tags</small>}</div>}
                </div>
                <span className="sr-only" role="status" aria-live="polite">{tagAnnouncement || (tagPickerOpen && tagInput ? `${matchingTags.length} matches${!exactTag ? "; a new Tag can be created" : ""}` : "")}</span>
                {createTag.isError && <span className="quick-inline-error" role="alert">{errorText(createTag.error, "save")} <button type="button" disabled={!tagInput.trim() || createTag.isPending} onClick={() => createTag.mutate(tagInput)}>Retry Tag Creation</button></span>}
              </div>
            </div>
          </section>
        </div>
      </fieldset>
      {mutation.isError && <p className="form-error" role="alert">{errorText(mutation.error, "save")}</p>}
    </Dialog>
  );
}

function locationFromPath(pathname: string) {
  const match = pathname.match(/^\/(projects|areas)\/([^/]+)$/);
  if (!match) return "";
  return `${match[1] === "projects" ? "project" : "area"}:${match[2]}`;
}

function scheduleFromPath(pathname: string): ScheduleKind | null {
  return ({ "/inbox": "inbox", "/today": "today", "/anytime": "anytime", "/someday": "someday" } as Partial<Record<string, ScheduleKind>>)[pathname] ?? null;
}

function taskDetailDestination(pathname: string, scheduleKind: ScheduleKind) {
  if (/^\/(projects|areas|tags)\/[a-zA-Z0-9-]+$/.test(pathname)) return pathname;
  return ({ inbox: "/inbox", today: "/today", anytime: "/anytime", someday: "/someday", date: "/upcoming" } as Record<ScheduleKind, string>)[scheduleKind];
}

function hasDraft(value: { title: string; locationValue: string; startDate: string; deadline: string; notes: string; checklist: string; tagIds: string[]; tagInput: string }) {
  return Boolean(value.title.trim() || value.locationValue || value.startDate || value.deadline || value.notes.trim() || value.checklist.trim() || value.tagIds.length || value.tagInput.trim());
}

function normalizeTagName(name: string) {
  return name.trim().toLocaleLowerCase("en-US");
}

function resizeTextarea(element: HTMLTextAreaElement | null, maxLines?: number) {
  if (!element) return;
  element.style.height = "auto";
  if (!maxLines) {
    element.style.height = `${element.scrollHeight}px`;
    return;
  }
  const styles = window.getComputedStyle(element);
  const lineHeight = Number.parseFloat(styles.lineHeight) || 24;
  const verticalPadding = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
  const maxHeight = lineHeight * maxLines + verticalPadding;
  element.style.height = `${Math.min(element.scrollHeight, maxHeight)}px`;
  element.style.overflowY = element.scrollHeight > maxHeight ? "auto" : "hidden";
}

